import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useHomeworks, useHomeworkHelp, useHomeworkDocument } from '@/services/homeworks';
import { AiMarkdownRenderer } from '@/components/ui/AiMarkdownRenderer';
import { GlobalHeader } from '@/components/GlobalHeader';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { Spinner } from '@/components/ui/Spinner';

type Message = { role: 'user' | 'assistant'; content: string };

export function HomeworkChatPage() {
  const { t } = useTranslation();
  const { id: homeworkId, eid: exerciseId } = useParams<{ id: string; eid: string }>();
  const { childId } = useAuthStore();
  const { data } = useHomeworks(childId ?? '');
  const { mutateAsync: askHelp, isPending } = useHomeworkHelp();

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [showDoc, setShowDoc] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const homework = data?.homeworks.find((h) => h.homework_id === homeworkId);
  const exercise = homework?.exercises.find((e) => e.exercise_id === exerciseId);

  const { data: docData } = useHomeworkDocument(
    childId ?? '',
    homeworkId ?? '',
    showDoc,
  );

  // For PDFs: open in new tab when presigned URL arrives
  useEffect(() => {
    if (docData?.media_type === 'application/pdf') {
      window.open(docData.document_url, '_blank');
      setShowDoc(false);
    }
  }, [docData]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isPending]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isPending || !homeworkId || !exerciseId) return;
    setInput('');
    setError('');

    const next: Message[] = [...messages, { role: 'user', content: text }];
    setMessages(next);

    try {
      const { reply } = await askHelp({
        homework_id: homeworkId,
        exercise_id: exerciseId,
        message: text,
        history: messages.slice(-10),
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setError(msg.includes('429') ? t('ai_session.limit_reached') : t('homework.chat_error'));
      setMessages(messages);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute={`/student/homework/${homeworkId}`} />

        {/* app-content overridden: chat needs its own scroll region inside */}
        <div
          className="app-content"
          style={{ padding: 0, gap: 0, overflowY: 'hidden' }}
        >
          {/* ── Info header: subject + exercise prompt ── */}
          <div style={{
            flexShrink: 0,
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            background: 'white',
          }}>
            <h1 style={{
              fontSize: '1.1rem', fontWeight: 700,
              color: 'var(--text-dark)', margin: '0 0 4px',
            }}>
              {homework?.subject ?? t('homework.help_title')}
            </h1>
            {exercise ? (
              <p style={{
                margin: 0, fontSize: '0.82rem',
                color: 'var(--text-muted)', lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              } as React.CSSProperties}>
                {exercise.prompt}
              </p>
            ) : !data ? (
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>{t('homework.loading_exercise')}</p>
            ) : null}
            {/* Document link — only if the homework has an attachment */}
            {homework?.has_document && (
              <button
                type="button"
                onClick={() => setShowDoc(true)}
                style={{
                  background: 'none', border: 'none', color: 'var(--primary)',
                  fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                  padding: '4px 0 0', fontFamily: 'inherit',
                }}
              >
                {t('homework.see_document')}
              </button>
            )}
          </div>

          {/* ── Chat messages ── */}
          <div style={{
            flex: 1, minHeight: 0, overflowY: 'auto',
            padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', paddingTop: '32px' }}>
                <p style={{ fontSize: '0.9rem', margin: '0 0 4px' }}>{t('homework.chat_prompt')}</p>
                <p style={{ fontSize: '0.8rem', margin: 0 }}>{t('homework.chat_guide')}</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div style={{
                  maxWidth: '82%',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  padding: '10px 14px',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  background: msg.role === 'user' ? 'var(--primary)' : 'white',
                  color: msg.role === 'user' ? 'white' : 'var(--text-dark)',
                  boxShadow: msg.role === 'assistant' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
                }}>
                  {msg.role === 'assistant'
                    ? <AiMarkdownRenderer content={msg.content} />
                    : msg.content}
                </div>
              </div>
            ))}

            {isPending && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  background: 'white', border: '1px solid var(--border)',
                  borderRadius: '16px 16px 16px 4px', padding: '10px 16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                }}>
                  <Spinner size="sm" />
                </div>
              </div>
            )}

            {error && (
              <p style={{ fontSize: '0.8rem', color: 'var(--danger)', textAlign: 'center', margin: 0 }}>
                {error}
              </p>
            )}

            <div ref={bottomRef} />
          </div>

          {/* ── Input bar ── */}
          <div style={{
            flexShrink: 0,
            padding: '10px 16px 14px',
            borderTop: '1px solid var(--border)',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <input
              type="text"
              className="input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={t('homework.chat_placeholder')}
              disabled={isPending}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSend}
              disabled={isPending || !input.trim()}
              style={{ width: '100%' }}
            >
              {t('homework.chat_send')}
            </button>
          </div>
        </div>

        <BottomTabBar activeTab="Home" />
      </div>

      {/* Full-screen image overlay (images only; PDFs open in new tab) */}
      {showDoc && docData && docData.media_type.startsWith('image/') && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowDoc(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '20px',
          }}
        >
          <img
            src={docData.document_url}
            alt={t('homework.document_alt')}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
          />
          <button
            type="button"
            onClick={() => setShowDoc(false)}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'white', border: 'none', borderRadius: '50%',
              width: '36px', height: '36px', fontSize: '1.3rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
